import { useEffect, useState } from 'react'

import TemplateDto from '../interfaces/TemplateDto'
import Client from '../interfaces/Client'
import { useAuth } from '../hooks/useAuth'
import useTemplateFetcher from '../hooks/useTemplateFetcher'
import { AppPath } from '../enums/AppPath'
import VehicleBase from '../interfaces/VehicleBase'
import { NvvAction, ReservaAction } from '../enums/DocActions'
import { DEFAULT_TITLE_CONFIG, TitleConfig } from '../utils/documentTitleUtils'

import DocConfirma from './DocConfirma'
import { ReservationFormat } from './ReservationNvvEdit'
import { ButtonAction } from './Buttons'
import DocFooter from './DocFooter'
import ConfirmationModal from './ConfirmationModal'
import ReservationNvvDetail from './ReservationNvvDetail'
import { PrintableCard } from './PrintableCard'

export interface ReservationDet extends ReservationFormat {
  titleConfig?: TitleConfig
  body: {
    vehicle: VehicleBase | null
    organization: {
      pictures: { logotype: string; isotype: string }
      nin: string
      legalName: string
      name: string
      business: string
      phone: string
      email: string
      domain: string
      address: string
    }
    client: {
      nin: string
      name: string
      phone: string
      email: string
      address: string
    }
  }
}

interface Props {
  vehicle: VehicleBase
  client: Client
  reservation: ReservationFormat
  creandoSw: boolean
  modificandoSw: boolean
  onGoBack: () => void
  onCrearClick: () => void
  onCancelModify: () => void
  nvvInd: number
  loading?: boolean
}

export const ReservationPreview = ({
  vehicle,
  client,
  reservation,
  creandoSw,
  modificandoSw,
  onGoBack,
  onCrearClick,
  onCancelModify,
  nvvInd,
  loading,
}: Props) => {
  const { userData } = useAuth()
  const { errorTemplate, fetchTemplate } = useTemplateFetcher()
  const [template, setTemplate] = useState<TemplateDto | null>(null)
  const organizationId = userData?.organization?._id ?? ''
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    const fetchDataAsync = async () => {
      const docType = nvvInd === NvvAction.SoloReserva ? AppPath.Reservations : AppPath.SaleNotes
      const data = organizationId ? await fetchTemplate(organizationId, docType) : null
      setTemplate(data)
    }
    fetchDataAsync()
  }, [organizationId])

  let buttonAction

  if (nvvInd === NvvAction.CreaFromRes || nvvInd === NvvAction.SoloReserva) {
    buttonAction = modificandoSw ? ReservaAction.Modificar : ReservaAction.Crear
  } else {
    buttonAction = modificandoSw ? NvvAction.Modificar : NvvAction.Crear
  }

  const onCancelModClick = () => {
    setIsModalOpen(true)
  }
  const handleConfirm = () => {
    setIsModalOpen(false)
    // Perform delete action here
    onCancelModify()
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  // Rellenamos con los datos devueltos desde el template, el cliente y
  // el vehiculo
  reservation.footer =
    nvvInd === NvvAction.SoloReserva ? template?.reservations.footer.value : template?.salenotes.footer.value

  // Extract title configuration from template based on document type
  // For existing documents, use the titleConfig from reservation (fetched by backend)
  // For new documents, use the titleConfig from template
  const titleConfig =
    reservation.titleConfig ??
    (nvvInd === NvvAction.SoloReserva
      ? (template?.reservations?.title ?? { ...DEFAULT_TITLE_CONFIG })
      : (template?.salenotes?.title ?? { ...DEFAULT_TITLE_CONFIG }))

  const org = template?.general.organization
  const reservationDet: ReservationDet = {
    ...reservation,
    titleConfig,
    body: {
      vehicle: vehicle,
      organization: {
        pictures: org?.pictures ?? { logotype: '', isotype: '' },
        nin: org?.nin?.value ?? '',
        legalName: '',
        name: org?.name.value ?? '',
        business: org?.business.type !== '-' ? (org?.business.value ?? '') : '',
        phone: org?.phone.value ?? '',
        email: org?.email?.value ?? '',
        domain: org?.domain?.value ?? '',
        address: org?.address?.value ?? '',
      },

      client: {
        nin: client?.nin ?? '',
        name: `${client?.firstname} ${client?.lastname}`,
        phone: client?.phone ?? '',
        email: client?.email ?? '',
        address: client?.address ?? '',
      },
    },
  }
  const names = {
    profileName: reservationDet.profile_name ?? '',
    organizationName: reservationDet.body.organization.name,
    clientName: `${reservationDet.body.client.name}`,
  }
  const space = '\u00A0'
  const doc = nvvInd === NvvAction.SoloReserva ? 'reserva' : 'nota de venta'
  const buttonLabel = `${space}${space}Volver a la ${doc}${space}${space}`
  const doctype = nvvInd === NvvAction.SoloReserva ? AppPath.Reservations : AppPath.SaleNotes

  return (
    <PrintableCard>
      <ReservationNvvDetail reservation={reservationDet} nvvInd={nvvInd} />
      <DocFooter names={names} />

      {(creandoSw || modificandoSw) && (
        <DocConfirma docType={doctype} handleCrearClick={onCrearClick} action={buttonAction} loading={loading} />
      )}

      <div className="flex justify-center mt-1">
        {(creandoSw || modificandoSw) && (
          <div className="mr-4">
            <ButtonAction label={buttonLabel} handleButtonClick={onGoBack} />
          </div>
        )}
        {modificandoSw && <ButtonAction label={`Cancelar modificaciones`} handleButtonClick={onCancelModClick} />}
      </div>

      {errorTemplate && <div>Error: {errorTemplate.message}</div>}

      {isModalOpen && (
        <ConfirmationModal
          message="¿Estás seguro? Perderás los cambios hechos."
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </PrintableCard>
  )
}
